import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardHeader, CardBody, Row, Col, Form, FormGroup, Label, Input, Table } from 'reactstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { Btn, H5 } from '../../../AbstractElements';
import config from '../../../config';
import licenseService from '../../../Services/licenseService';

const authHeader = () => {
    const token = localStorage.getItem('sessiontoken');
    return { headers: { Authorization: `${token}` } };
};

const GroupsAdmin = ({ refreshKey }) => {
    const { t } = useTranslation();
    const concessionaire_id = localStorage.getItem('concessionaireId');
    const [groups, setGroups] = useState([]);
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(false);

    const [newName, setNewName] = useState('');
    const [newCode, setNewCode] = useState('');
    const [newMembers, setNewMembers] = useState([]);
    const [creating, setCreating] = useState(false);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [gRes, pRes] = await Promise.all([
                licenseService.listGroups(),
                axios.post(`${config.hostname}/property/getpropertybyconcessionaire`,
                    { property_concessionaire_id: concessionaire_id },
                    authHeader())
            ]);
            setGroups(gRes.data || []);
            setProperties(pRes.data || []);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [concessionaire_id]);

    useEffect(() => { loadAll(); }, [loadAll, refreshKey]);

    // Only offer properties that aren't already in a group. A property can
    // belong to at most one group, so exclude anyone already assigned.
    const propertyOptions = useMemo(
        () => properties
            .filter(p => !p.property_license_group_id)
            .map(p => ({
                value: p._id,
                label: `${p.property_name} · ${p.property_mall_code}`
            })),
        [properties]
    );

    const submitCreate = async (e) => {
        e.preventDefault();
        if (!newName.trim() || !newCode.trim()) {
            toast.error(t('Group name and code are required'));
            return;
        }
        setCreating(true);
        try {
            await licenseService.createGroup({
                group_name: newName.trim(),
                group_code: newCode.trim(),
                memberPropertyIds: newMembers
            });
            toast.success(t('Group created'));
            setNewName('');
            setNewCode('');
            setNewMembers([]);
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.message || t('Failed to create group'));
        } finally {
            setCreating(false);
        }
    };

    return (
        <>
            <Card>
                <CardHeader>
                    <H5 attrH5={{ className: 'mb-0' }}>{t('Create License Group')}</H5>
                    <div className='f-12 f-light mt-1'>
                        {t('Group multiple properties so their licenses come from one shared pool. Individual properties not added to any group keep their own asset-wise pool.')}
                    </div>
                </CardHeader>
                <CardBody>
                    <Form onSubmit={submitCreate}>
                        <Row>
                            <Col md='5'>
                                <FormGroup>
                                    <Label>{t('Group Name')}</Label>
                                    <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder={t('e.g. Aldar Community Malls')} />
                                </FormGroup>
                            </Col>
                            <Col md='4'>
                                <FormGroup>
                                    <Label>{t('Group Code')}</Label>
                                    <Input value={newCode} onChange={e => setNewCode(e.target.value)} placeholder={t('e.g. aldar-community')} />
                                </FormGroup>
                            </Col>
                            <Col md='3' className='d-flex align-items-end'>
                                <Btn attrBtn={{ color: 'primary', type: 'submit', disabled: creating, className: 'mb-3' }}>
                                    {creating ? t('Creating…') : t('Create Group')}
                                </Btn>
                            </Col>
                        </Row>
                        <Row>
                            <Col md='12'>
                                <FormGroup>
                                    <Label>{t('Initial Members (optional)')}</Label>
                                    <Select
                                        isMulti
                                        isSearchable
                                        closeMenuOnSelect={false}
                                        placeholder={t('Search and select properties…')}
                                        options={propertyOptions}
                                        value={propertyOptions.filter(o => newMembers.includes(o.value))}
                                        onChange={(selected) => setNewMembers((selected || []).map(o => o.value))}
                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                        menuPosition='fixed'
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </Form>
                </CardBody>
            </Card>

            <Card className='mt-3'>
                <CardHeader>
                    <H5 attrH5={{ className: 'mb-0' }}>{t('Existing Groups')}</H5>
                </CardHeader>
                <CardBody>
                    {loading && <div className='f-light'>{t('Loading…')}</div>}
                    {!loading && groups.length === 0 && <div className='f-light'>{t('No groups defined.')}</div>}
                    {groups.map(g => {
                        const members = g.members || [];
                        return (
                            <Card key={g._id} className='mb-3'>
                                <CardBody>
                                    <H5>{g.group_name}</H5>
                                    <div className='f-12 f-light'>
                                        {t('Code')}: <code>{g.group_code}</code> · {t('{{count}} member properties', { count: members.length })}
                                    </div>
                                    {members.length === 0 ? (
                                        <div className='f-light mt-3'>{t('No member properties assigned.')}</div>
                                    ) : (
                                        <Table size='sm' className='mt-3 mb-0'>
                                            <thead>
                                                <tr>
                                                    <th>{t('Property')}</th>
                                                    <th>{t('Mall Code')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {members.map(m => (
                                                    <tr key={m._id}>
                                                        <td>{m.property_name}</td>
                                                        <td>{m.property_mall_code}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    )}
                                </CardBody>
                            </Card>
                        );
                    })}
                </CardBody>
            </Card>
        </>
    );
};

export default GroupsAdmin;
